import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField, SelectField } from "../v3";
import { AdminService, ProjectService, PurchaseService, TaskService } from "../../services";
import { taskValidationRules } from "../../pages/task/validation";

export const AddEditTask = ({
  open = false,
  projectId = null,
  onClose = () => {},
  taskId = null,
  taskProgressQuantity = 0,
  onSuccess = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectStage, setSelectedProjectStage] = useState(null);
  const [projectStages, setProjectStages] = useState([]);
  const [stageIds, setStageIds] = useState([]);
  const [unitSelectOptions, setUnitSelectOptions] = useState([]);
  const [taskDetails, setTaskDetails] = useState({
    name: undefined,
    quantity: undefined,
    unitOfMeasure: undefined,
    assignedToId: undefined,
    projectId: undefined,
    projectStageId: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  useEffect(() => {
    if (open && taskId) fetchData(Number(taskId));
    else if (open && projectId && !taskId) fetchProject(projectId);
    else if (open && !taskId) handleClearForm();
  }, [open, taskId, projectId]);

  useEffect(() => {
    if (open) fetchUnits();
  }, [open]);

  const stageFilter = useMemo(
    () => ({
      ids: stageIds,
    }),
    [stageIds],
  );

  const fetchData = async (taskId) => {
    try {
      const { data: task } = await TaskService.getTaskById(taskId);

      // fetch project stages
      if (task?.projectId) fetchProjectStages(task?.projectId);

      // fetch stages
      if (task?.projectStageId) {
        try {
          const { data: stage } = await ProjectService.getStageById(task?.projectStage?.stageId);
          setSelectedProjectStage(stage);
        } catch (error) {
          toast.error("Failed to fetch stage details");
          console.error("🚀 ~ fetchData ~ error:", error);
        }
      }

      setTaskDetails({
        name: task?.name,
        quantity: task?.quantity,
        unitOfMeasure: task?.unitOfMeasure,
        assignedToId: task?.assignedToId,
        projectId: task?.projectId,
        projectStageId: task?.projectStageId,
        startDate: task?.startDate,
        endDate: task?.endDate,
      });
      setSelectedAssignee(task?.assignedToUser);
      setSelectedProject(task?.project);
    } catch (error) {
      toast.error("Failed to fetch task details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const fetchProjectStages = async (projectId) => {
    try {
      const { data: first } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: 10,
      });

      const { data } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: first.count,
      });

      const stages = [...new Set(data.rows.map((item) => item.stageId))];

      setStageIds(stages.join(","));
      setProjectStages(data.rows);
    } catch (error) {
      console.error("🚀 ~ fetchProjectStages ~ error:", error);
    }
  };

  const fetchProject = async (projectId) => {
    try {
      const { data: project } = await ProjectService.getProjectById(projectId);
      handleProjectChange(project);
    } catch (error) {
      console.log("🚀 ~ fetchProject ~ error:", error);
    }
  };

  const fetchUnits = async () => {
    try {
      const { data } = await PurchaseService.getUnitOfMeasurements();
      const rows = Array.isArray(data) ? data : data?.rows || [];
      setUnitSelectOptions(rows.map((item) => ({ label: item.name, value: item.name })));
    } catch (error) {
      console.error("Failed to fetch units", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setTaskDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "quantity") {
      const num = parseFloat(value);
      if (value !== "" && (isNaN(num) || num <= 0)) {
        setErrors((prev) => ({ ...prev, quantity: "must be greater than 0" }));
      } else {
        setErrors((prev) => {
          const { quantity, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleAssigneeChange = (selected) => {
    setSelectedAssignee(selected);
    setTaskDetails((prev) => ({ ...prev, assignedToId: selected?.id || null }));
  };

  const handleProjectChange = (selected) => {
    setStageIds([]);
    setSelectedProjectStage(null);
    fetchProjectStages(selected?.id);
    setSelectedProject(selected);
    setTaskDetails((prev) => ({ ...prev, projectId: selected?.id || null }));
  };

  const handleProjectStageChange = (selected) => {
    const projectStage = projectStages.find((item) => item?.stageId === selected?.id);
    setSelectedProjectStage(selected);
    setTaskDetails((prev) => ({ ...prev, projectStageId: projectStage?.id || null }));
  };

  const handleClearForm = () => {
    setTaskDetails({
      name: undefined,
      assignedToId: undefined,
      projectId: undefined,
      projectStageId: undefined,
      startDate: undefined,
      endDate: undefined,
      quantity: undefined,
      unitOfMeasure: undefined,
    });
    setSelectedAssignee(null);
    setSelectedProject(null);
    setSelectedProjectStage(null);
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };
  const handleSaveTask = async () => {
    setErrors({});

    const validationErrors = taskValidationRules(taskDetails);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (taskDetails.quantity) taskDetails.quantity = Number(taskDetails.quantity);
    const payload = {
      ...taskDetails,
      startDate: taskDetails.startDate || null,
      endDate: taskDetails.endDate || null,
    };
    try {
      setLoading(true);
      if (taskId) {
        if (taskDetails.quantity === taskProgressQuantity) {
          await TaskService.updateTask(taskId, { ...payload, status: "DONE" });
        } else if (taskDetails.quantity < taskProgressQuantity) {
          toast.warning(
            `Quantity(${taskDetails.quantity}) is less than task progress quantity(${taskProgressQuantity}), please review the progress entries`,
          );
          return;
        } else if ((taskDetails.quantity > taskProgressQuantity) & (taskProgressQuantity !== 0)) {
          await TaskService.updateTask(taskId, { ...payload, status: "INPROGRESS" });
        } else {
          await TaskService.updateTask(taskId, { ...payload });
        }
      } else await TaskService.createTask(taskDetails);

      toast.success(`Task ${taskId ? "updated" : "created"} successfully`);
      onSuccess?.();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task.");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };
  return (
    <FormDialog
      open={open}
      title={taskId ? "Edit Task" : "Add Task"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveTask} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Name"
            name="name"
            value={taskDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <InputField
            required
            type="number"
            label=" Quantity"
            name="quantity"
            value={taskDetails.quantity}
            onChange={updateDetailsState}
            error={!!errors.quantity}
            helperText={errors.quantity}
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            select
            required
            label="Unit"
            name="unitOfMeasure"
            value={taskDetails.unitOfMeasure || ""}
            selectOptions={unitSelectOptions}
            onChange={updateDetailsState}
            error={!!errors.unitOfMeasure}
            helperText={errors.unitOfMeasure}
            fullWidth
          />
          <SelectField
            label="Assignee"
            value={selectedAssignee}
            getData={AdminService.getUserList}
            getDataStaticFilters={{ type: "USER" }}
            optionValueFields={["firstName", "lastName"]}
            error={!!errors.assigneeId}
            helperText={errors.assigneeId}
            onChange={handleAssigneeChange}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            label="Project"
            value={selectedProject}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ status: ["INPROGRESS"] }}
            disabled={!!projectId}
            error={!!errors.projectId}
            helperText={errors.projectId}
            onChange={handleProjectChange}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Stage"
            value={selectedProjectStage}
            getData={ProjectService.getStages}
            getDataStaticFilters={stageFilter}
            disabled={stageIds.length === 0}
            error={!!errors.projectStageId}
            helperText={errors.projectStageId}
            onChange={handleProjectStageChange}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Start Date"
            type="date"
            name="startDate"
            value={taskDetails.startDate}
            onChange={updateDetailsState}
            error={!!errors.startDate}
            helperText={errors.startDate}
            fullWidth
          />
          <InputField
            label="End Date"
            type="date"
            name="endDate"
            value={taskDetails.endDate}
            onChange={updateDetailsState}
            error={!!errors.endDate}
            helperText={errors.endDate}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
